import React, { useEffect, useState, useContext } from 'react';
import { Progress } from 'antd';
import CircularLoader from '@common/CircularLoader';
import axios from '@common/axios';
import lessonPlannerEventBus from '../lessonPlannerEventBus';
import { UserContext } from '@context/UserContext';

const STATES = [
    { key: 'planned',  label: 'Planned',     color: '#1677ff' },
    { key: 'active',   label: 'In Progress', color: '#fa8c16' },
    { key: 'complete', label: 'Complete',    color: '#52c41a' },
];

const LessonPlannerReportDrawer = () => {
    const { user } = useContext(UserContext);
    const [plan, setPlan] = useState(null);
    const [topicGroups, setTopicGroups] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const scheduleId = lessonPlannerEventBus.scheduleId;
        if (!scheduleId) return;
        setLoading(true);
        Promise.all([
            axios.get(`/web/ui/lessonScheduler/plans/${scheduleId}`),
            user?.syllabusId ? axios.get(`/web/ui/nav/syllabus/${user.syllabusId}/topicGroups`) : Promise.resolve({ data: [] }),
        ]).then(([planRes, topicRes]) => {
            setPlan(planRes.data);
            setTopicGroups(topicRes.data);
        }).finally(() => setLoading(false));
    }, []);

    if (loading) return <CircularLoader pageCentered />;

    const allLessons = (plan?.units || []).flatMap(u => u.lessons || []);
    const total = allLessons.length;

    const complete = allLessons.filter(l =>
        (l.classStatuses || []).length > 0 &&
        (l.classStatuses || []).every(cs => cs.lessonState === 'COMPLETE')
    ).length;

    const active = allLessons.filter(l =>
        (l.classStatuses || []).some(cs => cs.lessonState === 'ACTIVE') &&
        !(l.classStatuses || []).every(cs => cs.lessonState === 'COMPLETE')
    ).length;

    const planned = total - complete - active;
    const donePct = total > 0 ? Math.round((complete / total) * 100) : 0;
    const counts = { planned, active, complete };

    const allConceptIds = topicGroups.flatMap(g => g.topics || []).flatMap(t => t.concepts || []).map(c => c.conceptId);
    const coveredIds = new Set(allLessons.flatMap(l => (l.concepts || []).map(c => c.conceptId)));
    const coveredCount = allConceptIds.filter(id => coveredIds.has(id)).length;
    const totalConcepts = allConceptIds.length;
    const coveragePct = totalConcepts > 0 ? Math.round((coveredCount / totalConcepts) * 100) : 0;

    return (
        <div className="ch-lesson-planner-drawer">
            <h3 className="ch-lesson-planner-drawer-title">Schedule Report</h3>

            <section className="ch-lesson-planner-report-section" aria-labelledby="report-completion-heading">
                <h4 id="report-completion-heading" className="ch-lesson-planner-detail-section-heading">Completion</h4>

                <div
                    className="ch-lesson-planner-report-bar"
                    role="meter"
                    aria-valuenow={complete}
                    aria-valuemin={0}
                    aria-valuemax={total}
                    aria-label={`Overall completion: ${complete} of ${total} lessons done`}
                >
                    <Progress
                        percent={donePct}
                        showInfo={false}
                        strokeColor={donePct === 100 ? '#52c41a' : '#1677ff'}
                        railColor="rgba(0,0,0,0.12)"
                    />
                    <div className="ch-lesson-planner-report-bar-labels">
                        <span>{complete} / {total} lessons done</span>
                        <span>{donePct}%</span>
                    </div>
                </div>

                <h4 id="report-states-heading" className="ch-lesson-planner-detail-section-heading">Lesson Status</h4>

                <div aria-labelledby="report-states-heading" aria-label={`${planned} planned, ${active} in progress, ${complete} complete`}>
                    <div className="ch-lesson-planner-report-segmented-bar" role="presentation">
                        {STATES.map(({ key, color }) => {
                            const pct = total > 0 ? (counts[key] / total) * 100 : 0;
                            return pct > 0 ? (
                                <div key={key} style={{ width: `${pct}%`, background: color, transition: 'width 0.3s' }} />
                            ) : null;
                        })}
                    </div>
                    <ul className="ch-lesson-planner-report-state-list">
                        {STATES.map(({ key, label, color }) => (
                            <li key={key} className="ch-lesson-planner-report-state-item">
                                <span>
                                    <span aria-hidden="true" className="ch-lesson-planner-report-state-dot" style={{ color }}>&#9679;</span>
                                    {label}
                                </span>
                                <span className="ch-lesson-planner-report-state-count">
                                    {counts[key]} lesson{counts[key] !== 1 ? 's' : ''}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {totalConcepts > 0 && (
                <section className="ch-lesson-planner-report-section" aria-labelledby="report-coverage-heading">
                    <h4 id="report-coverage-heading" className="ch-lesson-planner-detail-section-heading">Syllabus Coverage</h4>
                    <div
                        className="ch-lesson-planner-report-bar"
                        role="meter"
                        aria-valuenow={coveredCount}
                        aria-valuemin={0}
                        aria-valuemax={totalConcepts}
                        aria-label={`Syllabus coverage: ${coveredCount} of ${totalConcepts} concepts covered`}
                    >
                        <Progress
                            percent={coveragePct}
                            showInfo={false}
                            strokeColor={coveragePct === 100 ? '#52c41a' : 'var(--secondary-colour)'}
                            railColor="rgba(0,0,0,0.12)"
                        />
                        <div className="ch-lesson-planner-report-bar-labels">
                            <span>{coveredCount} / {totalConcepts} concepts covered</span>
                            <span>{coveragePct}%</span>
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
};

export default LessonPlannerReportDrawer;
